import React from 'react';
import { Typography } from 'src/components/Typography';
import { formatFullDate } from 'src/utils/dateFormatter';

interface RecurringDescriptionProps {
  startAt?: string;
  endAt?: string | null;
  recurringType: string;
  recurringDayNumbers: number[];
  recurringCount: number;
  days: number[];
  variant?: 'appointment' | 'member';
  inline?: boolean;
}

const DATES = [
  { id: 1, name: '1st' },
  { id: 2, name: '2nd' },
  { id: 3, name: '3rd' },
  { id: 4, name: '4th' },
  { id: 5, name: '5th' },
  { id: 6, name: '6th' },
  { id: 7, name: '7th' },
  { id: 8, name: '8th' },
  { id: 9, name: '9th' },
  { id: 10, name: '10th' },
  { id: 11, name: '11th' },
  { id: 12, name: '12th' },
  { id: 13, name: '13th' },
  { id: 14, name: '14th' },
  { id: 15, name: '15th' },
  { id: 16, name: '16th' },
  { id: 17, name: '17th' },
  { id: 18, name: '18th' },
  { id: 19, name: '19th' },
  { id: 20, name: '20th' },
  { id: 21, name: '21st' },
  { id: 22, name: '22nd' },
  { id: 23, name: '23rd' },
  { id: 24, name: '24th' },
  { id: 25, name: '25th' },
  { id: 26, name: '26th' },
  { id: 27, name: '27th' },
  { id: 28, name: '28th' },
  { id: 29, name: '29th' },
  { id: 30, name: '30th' },
  { id: 31, name: '31st' },
];

const DAY_NUMBERS = [
  { id: 1, name: '1st' },
  { id: 2, name: '2nd' },
  { id: 3, name: '3rd' },
  { id: 4, name: '4th' },
  { id: -1, name: 'Last' },
];

const WEEKDAYS = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
];

export const RecurringDescription: React.FC<RecurringDescriptionProps> = ({
  startAt,
  endAt,
  recurringType,
  recurringDayNumbers,
  recurringCount,
  days = [],
  variant = 'appointment',
  inline = false,
}) => {
  const formatDescription = () => {
    let description = '';
    let normalizedRecurringType = recurringType;

    if (['month_on_date', 'month_on_day'].includes(recurringType)) {
      normalizedRecurringType = 'month';
    }

    // Format dates based on variant
    if (variant === 'appointment') {
      let dates = 'Repeat forever ';
      if (startAt || endAt) {
        dates = `${startAt ? formatFullDate(startAt) : 'Indefinite'} to ${
          endAt ? formatFullDate(endAt) : 'indefinite'
        } `;
      }
      description += dates;
    } else {
      let dates = 'Duration of the Recurring Session ';
      if (startAt || endAt) {
        dates = `${
          startAt ? formatFullDate(startAt) : 'Start of Recurring Session'
        } to ${endAt ? formatFullDate(endAt) : 'End of Recurring Session'} `;
      }
      description += dates;
    }

    // Add recurring count and type
    const separator = inline ? ', ' : '<br>';
    description += `${separator}every ${
      recurringCount > 1 ? `${recurringCount} ` : ''
    }${normalizedRecurringType}${recurringCount > 1 ? 's' : ''}`;

    // Handle month_on_day with both day numbers and weekdays
    if (recurringType === 'month_on_day' && recurringDayNumbers.length) {
      const dateList = recurringDayNumbers
        .map((dayNumber) => DAY_NUMBERS.find((d) => d.id === dayNumber)?.name)
        .filter(Boolean);

      const dayList =
        Array.isArray(days) && days.length > 0 && typeof days[0] === 'number'
          ? days.map((day) => WEEKDAYS[day].toLowerCase())
          : days
              .filter((day: any) => day.tempSelected)
              .map((day: any) => day.name.toLowerCase());

      if (dateList.length === 1) {
        description += ` on the ${dateList[0]}`;

        if (dayList.length === 1) {
          description += ` ${dayList[0]}`;
        } else if (dayList.length > 1) {
          description += ` ${dayList.slice(0, -1).join(', ')} and ${
            dayList[dayList.length - 1]
          }`;
        }
      }
    }
    // Handle regular week days
    else if (recurringType === 'week') {
      const dayList = days.map((day) => WEEKDAYS[day]);
      const formattedDays =
        variant === 'appointment'
          ? dayList.map(
              (d) => d.charAt(0).toUpperCase() + d.slice(1).toLowerCase()
            )
          : dayList.map((d) => d.toLowerCase());

      if (formattedDays.length === 1) {
        description += ` on ${formattedDays[0]}`;
      } else if (formattedDays.length > 1) {
        description += ` on ${formattedDays.slice(0, -1).join(', ')} and ${
          formattedDays[formattedDays.length - 1]
        }`;
      }
    }

    // Handle month_on_date
    if (recurringType === 'month_on_date' && recurringDayNumbers.length) {
      const dateList = recurringDayNumbers
        .map((dayNumber) => DATES.find((d) => d.id === dayNumber)?.name)
        .filter(Boolean);

      if (dateList.length) {
        description += ` on the ${dateList.slice(0, -1).join(', ')}${
          dateList.length > 1
            ? ` and ${dateList[dateList.length - 1]}`
            : dateList[0]
        }`;
      }
    }

    return description;
  };

  return (
    <Typography
      variant="body2"
      color="text.secondary"
      dangerouslySetInnerHTML={{ __html: formatDescription() }}
    />
  );
};
